import { FC } from "react";
import { useSelector } from "react-redux";

import { MoreHoriz } from "@livechat/design-system-icons";
import {
  ActionMenu,
  ActionMenuItem,
  Button,
  DesignToken,
  Icon,
  Info,
  SpacingToken,
  Text,
  Tooltip,
} from "@livechat/design-system-react-components";

import Flex from "components/layout/Flex";
import { SettingsTableColumn as RawColumn, SettingsTableRow as Row } from "components/settings-table";
import { prepareMailboxAddress } from "helpers/mailboxes";
import { useAgentFlag } from "hooks/use-agent-flag";
import { selectAgents } from "store/entities/agents";
import { selectLicenseId } from "store/entities/license";
import { IMailbox } from "store/entities/mailboxes/interfaces";
import { selectCanManageCustomMailboxes } from "store/entities/mailboxes/selectors";
import { selectTeams } from "store/entities/teams";
import { withVar } from "themes/helpers";

import { AssignmentInfo } from "./AssignmentInfo";
import { ForwardingAddressSummary } from "./ForwardingAddressSummary";
import { StatusBadge } from "./StatusBadge";

const Column: FC<{
  children: React.ReactNode;
}> = ({ children }) => <RawColumn paddingDesktop="1em 1.5em">{children}</RawColumn>;

export interface IForwardingAddressEntryProps {
  inbox: IMailbox;
  onShowInstructions: (inboxAddress: string) => void;
  onDeleteInbox: (inboxId: string) => void;
  onEditInbox: (inboxId: string) => void;
  tooltipShown?: boolean;
  isDefault?: boolean;
}

export const ForwardingAddressEntry: FC<IForwardingAddressEntryProps> = ({
  inbox: {
    id,
    name,
    agentId,
    teamId,
    flags: { inboxUsed },
    description,
  },
  onShowInstructions,
  onDeleteInbox,
  onEditInbox,
  tooltipShown = false,
  isDefault = false,
}) => {
  const licenseId = useSelector(selectLicenseId);
  const agents = useSelector(selectAgents);
  const agent = agentId ? agents[agentId] : undefined;
  const team = useSelector(selectTeams)[teamId];
  const canManageInboxes = useSelector(selectCanManageCustomMailboxes);
  const [, setInstructionsTooltipClosed] = useAgentFlag("hasClosedForwardingInstructionsTooltip");

  const address = prepareMailboxAddress(licenseId, name);

  // FIXME: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  const deleteInbox = () => onDeleteInbox(id);
  // FIXME: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  const editInbox = () => onEditInbox(id);

  // FIXME: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  const handleShowInstructions = () => {
    setInstructionsTooltipClosed(true);
    onShowInstructions(address);
  };

  return (
    <Row>
      <Column>
        <ForwardingAddressSummary inboxAddress={address} description={description || ""} />
      </Column>
      <Column>
        <AssignmentInfo agent={agent} team={team} />
      </Column>
      <Column>
        <Flex gap={withVar(SpacingToken.Spacing4)} justifyContent="space-between">
          <Flex column gap={withVar(SpacingToken.Spacing1)}>
            <StatusBadge forwarded={inboxUsed ?? false} />
            <Tooltip
              triggerRenderer={() => (
                <Button
                  kind="link"
                  onClick={handleShowInstructions}
                  size="xcompact"
                  data-testid="read-instructions-button"
                >
                  Read forwarding instructions
                </Button>
              )}
              isVisible={tooltipShown && canManageInboxes}
              placement="bottom"
              kind="important"
            >
              <Info
                header="Forward a copy of emails"
                text="Set up the forwarding necessary to get messages from your current email address to HelpDesk."
                theme="important"
                closeWithX
                handleCloseAction={() => setInstructionsTooltipClosed(true)}
              />
            </Tooltip>
          </Flex>
          <ActionMenu
            triggerRenderer={<Button kind="plain" icon={<Icon source={MoreHoriz} />} />}
            options={[
              {
                key: "edit",
                element: <ActionMenuItem>Edit</ActionMenuItem>,
                onClick: editInbox,
                disabled: !canManageInboxes,
              },
              {
                key: "delete",
                element: (
                  <ActionMenuItem kind="warning">
                    <Text
                      customColor={withVar(
                        !canManageInboxes || isDefault
                          ? DesignToken.ActionNegativeDisabled
                          : DesignToken.ActionNegativeDefault
                      )}
                    >
                      Delete
                    </Text>
                  </ActionMenuItem>
                ),
                onClick: deleteInbox,
                disabled: !canManageInboxes || isDefault,
              },
            ]}
          />
        </Flex>
      </Column>
    </Row>
  );
};
